import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { Toast } from './shared/Toast';

/**
 * After Stripe Checkout returns to the same page (?wf_checkout=1&session_id=...),
 * confirm the session, refresh subscription, and clean the URL so the user stays in context.
 */
export default function CheckoutReturnHandler() {
	const location = useLocation();
	const navigate = useNavigate();
	const { confirmCheckoutSession } = useSubscription();
	const handledRef = useRef(null);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const flag = params.get('wf_checkout');
		const sessionId = params.get('session_id');

		if (flag === 'cancel') {
			params.delete('wf_checkout');
			const next = params.toString();
			navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
			Toast.info('Checkout canceled. You can try again anytime.');
			return;
		}

		if (flag !== '1' || !sessionId) return;
		if (handledRef.current === sessionId) return;
		handledRef.current = sessionId;

		(async () => {
			try {
				const result = await confirmCheckoutSession(sessionId);
				Toast.success(
					result?.message ||
						(result?.allowancesReset
							? 'Subscription updated. Your allowances have been reset — you can continue working.'
							: 'Subscription updated. You can continue working.')
				);
			} catch (err) {
				Toast.error(err?.response?.data?.message || err?.message || 'Could not confirm checkout');
			} finally {
				params.delete('wf_checkout');
				params.delete('session_id');
				const next = params.toString();
				navigate(
					{ pathname: location.pathname, search: next ? `?${next}` : '' },
					{ replace: true }
				);
			}
		})();
	}, [location.pathname, location.search, confirmCheckoutSession, navigate]);

	return null;
}
